/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

/**
 * KeyboardFocusHighlighter helps kb users view what interactive elements they have focus on.
 *
 * In an ideal world, this would not exist and our css styling would normally highlight
 * focused elements. But for now, lots of css rules disable focus rings.
 *
 * This is done as a quick way to make sure focus rings are correctly visible when using a kb,
 * without impacting touch/mouse users, and without having to change the whole codebase.
 */
import { components } from "app/common/ThemePrefs";

import { Disposable, dom, styled } from "grainjs";

export class KeyboardFocusHighlighter extends Disposable {
  constructor() {
    super();
    this.autoDispose(dom.onElem(window, "keydown", this._onKeyDown));
    this.autoDispose(dom.onElem(window, "touchstart", this._clear));
    this.autoDispose(dom.onElem(window, "mousedown", this._clear));
  }

  public isKeyboardUser = () => {
    return document.documentElement.classList.contains(cssKeyboardUser.className);
  };

  private _onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      document.documentElement.classList.add(cssKeyboardUser.className);
    }
  };

  private _clear = () => {
    document.documentElement.classList.remove(cssKeyboardUser.className);
  };
}

export const kbFocusHighlighterClass = "kb-focus-highlighter-group";

const cssKeyboardUser = styled("div", `
  & .${kbFocusHighlighterClass} :is(a, input, textarea, select, button, [tabindex="0"]):focus-visible {
    outline: 3px solid ${components.kbFocusHighlight} !important;
  }
`);
